"use client";

import * as React from "react";
import { Check, ChevronDown, Loader2 } from "lucide-react";

import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { useTranslation } from "react-i18next";

interface Item {
  id: string | number;
  name: string;
}

interface PaginatedSelectProps {
  placeholder?: string;
  triggerId?: string;
  fetchItems: (
    page: number,
    search?: string
  ) => Promise<{ items: Item[]; hasMore: boolean }>;
  onSelect: (item: Item | null) => void;
  value?: Item | null;
  defaultValue?: Item;
  disabled?: boolean;
  className?: string;
  isInvalid?: boolean;
}

type Action =
  | { type: "OPEN_DROPDOWN" }
  | { type: "CLOSE_DROPDOWN" }
  | { type: "SET_SEARCH"; payload: string }
  | { type: "LOAD_START" }
  | {
    type: "LOAD_SUCCESS";
    payload: {
      items: Item[];
      hasMore: boolean;
      page: number;
      isNewSearch: boolean;
    };
  }
  | { type: "LOAD_ERROR" }
  | { type: "SELECT_ITEM"; payload: Item | null };

interface State {
  open: boolean;
  items: Item[];
  loading: boolean;
  search: string;
  page: number;
  hasMore: boolean;
  selectedItem: Item | null;
  loadingRef: { current: boolean };
}

function reducer(state: State, action: Action): State {
  switch (action.type) {
    case "OPEN_DROPDOWN":
      return { ...state, open: true };
    case "CLOSE_DROPDOWN":
      return { ...state, open: false, search: "" };
    case "SET_SEARCH":
      return { ...state, search: action.payload };
    case "LOAD_START":
      state.loadingRef.current = true;
      return { ...state, loading: true };
    case "LOAD_SUCCESS": {
      state.loadingRef.current = false;
      const { items, hasMore, page, isNewSearch } = action.payload;
      const currentItems = isNewSearch ? [] : state.items;
      const newItems = items.filter(
        (newItem) => !currentItems.some((exItem) => exItem.id === newItem.id) && newItem.id !== state.selectedItem?.id
      );
      const combined = isNewSearch ? items : [...state.items, ...newItems];
      if (state.selectedItem && !combined.some(i => i.id === state.selectedItem?.id)) {
        const foundInPayload = items.find(i => i.id === state.selectedItem?.id);
        if (foundInPayload) {
          combined.unshift(foundInPayload);
        }
      }

      return {
        ...state,
        loading: false,
        items: isNewSearch ? items : [...state.items, ...newItems],
        hasMore,
        page,
      };
    }
    case "LOAD_ERROR":
      state.loadingRef.current = false;
      return { ...state, loading: false };
    case "SELECT_ITEM":
      return { ...state, selectedItem: action.payload };
    default:
      return state;
  }
}

export function PaginatedSelect({
  placeholder = "Select an item...",
  triggerId,
  fetchItems,
  onSelect,
  value,
  defaultValue,
  disabled = false,
  className,
  isInvalid = false,
}: PaginatedSelectProps) {
  const i18n = useTranslation();
  const isMounted = React.useRef(false);
  const initialLoadDone = React.useRef(false);
  const searchTimerRef = React.useRef<NodeJS.Timeout | null>(null);
  const listRef = React.useRef<HTMLDivElement>(null);
  const loadingRef = React.useRef(false);

  const initialState: State = {
    open: false,
    items: [],
    loading: false,
    search: "",
    page: 1,
    hasMore: true,
    selectedItem: value !== undefined ? null : defaultValue || null,
    loadingRef,
  };

  const [state, dispatch] = React.useReducer(reducer, initialState);
  const { open, items, loading, search, hasMore, selectedItem } = state; // Removed 'page' here

  const loadItems = React.useCallback(
    async (pageNum: number, searchTerm: string, isNewSearch: boolean) => {
      if (loadingRef.current || (!hasMore && !isNewSearch)) return;

      dispatch({ type: "LOAD_START" });

      try {
        const result = await fetchItems(pageNum, searchTerm);

        if (isMounted.current) {
          dispatch({
            type: "LOAD_SUCCESS",
            payload: {
              items: result.items,
              hasMore: result.hasMore,
              page: pageNum,
              isNewSearch,
            },
          });
        }
      } catch {
        if (isMounted.current) {
          dispatch({ type: "LOAD_ERROR" });
        }
      }
    },
    [fetchItems, hasMore]
  );

  const handleOpenChange = (open: boolean) => {
    if (open) {
      dispatch({ type: "OPEN_DROPDOWN" });
      if (listRef.current) {
        listRef.current.scrollTop = 0;
      }
    } else {
      dispatch({ type: "CLOSE_DROPDOWN" });
      initialLoadDone.current = false;
    }
  };

  const handleSearchChange = (searchValue: string) => {
    dispatch({ type: "SET_SEARCH", payload: searchValue });

    if (searchTimerRef.current) {
      clearTimeout(searchTimerRef.current);
    }

    searchTimerRef.current = setTimeout(() => {
      if (isMounted.current && state.open) {
        initialLoadDone.current = false;
        loadItems(1, searchValue, true);
      }
    }, 300);
  };

  const handleSelect = (item: Item | null) => {
    if (value === undefined) {
      dispatch({ type: "SELECT_ITEM", payload: item });
    }
    onSelect(item);
    handleOpenChange(false);
  };

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    if (!state.open || state.loading || !state.hasMore) return;

    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;

    if (scrollHeight - scrollTop <= clientHeight + 50) {
      loadItems(state.page + 1, state.search, false); // Use state.page here
    }
  };

  React.useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
      if (searchTimerRef.current) {
        clearTimeout(searchTimerRef.current);
      }
    };
  }, []);

  React.useEffect(() => {
    if (state.open && !initialLoadDone.current && !loadingRef.current) {
      if (state.items.length === 0 || state.search !== '') {
        initialLoadDone.current = true;
        loadItems(1, state.search, true);
      } else {
        initialLoadDone.current = true;
      }
    }
  }, [state.open, state.items.length, state.search, loadItems]);


  React.useEffect(() => {
    if (value !== undefined) {
      if (value?.id !== selectedItem?.id) {
        dispatch({ type: "SELECT_ITEM", payload: value });

        if (value && !items.some(i => i.id === value.id) && !open) {
          if (!loadingRef.current) {
            // Selection updated internally, display name will be correct.
            // Rely on normal load mechanism when opened.
          }
        }
      }
    }
  }, [value, selectedItem?.id, items, open]);

  const displayItem = value !== undefined ? value : selectedItem;

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          data-testid={triggerId}
          aria-invalid={isInvalid || undefined}
          className={cn(
            "h-9 justify-between px-3 font-normal text-sm border bg-white shadow-none hover:bg-white",
            isInvalid
              ? "border-destructive/60 text-destructive hover:border-destructive focus-visible:ring-destructive/40"
              : "border-input hover:border-muted-foreground/40",
            className
          )}
        >
          {displayItem ? displayItem.name : placeholder}
          <ChevronDown className="ms-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] p-0">
        <Command shouldFilter={false}>
          <CommandInput
            placeholder={i18n.t("search_by_name")}
            value={search}
            onValueChange={handleSearchChange}
          />
          <CommandList ref={listRef} onScroll={handleScroll}>
            <CommandEmpty>
              {!loading && items.length === 0 ? i18n.t("no_results") : null}
            </CommandEmpty>
            <CommandGroup>
              {selectedItem && !items.some(i => i.id === selectedItem.id) && (
                <CommandItem
                  key={`selected-${selectedItem.id}`}
                  value={String(selectedItem.id)}
                  onSelect={() => handleSelect(selectedItem)}
                  className="opacity-75"
                >
                  <Check className={cn("me-2 h-4 w-4 opacity-100")} />
                  {selectedItem.name}
                </CommandItem>
              )}
              {items.map((item) => (
                <CommandItem
                  key={item.id}
                  value={String(item.id)}
                  onSelect={() => handleSelect(item)}
                >
                  <Check
                    className={cn(
                      "me-2 h-4 w-4",
                      displayItem?.id === item.id
                        ? "opacity-100"
                        : "opacity-0"
                    )}
                  />
                  {item.name}
                </CommandItem>
              ))}
              {loading && (
                <div className="flex items-center justify-center p-2 text-sm text-muted-foreground">
                  <Loader2 className="me-2 h-4 w-4 animate-spin" />
                  {i18n.t("loading")}
                </div>
              )}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

export default PaginatedSelect;